export class HttpRoutingLocation {
  path: string;
  port: number;
  config: HttpRoutingLocationConfig;
  serviceStackId: string;
  serviceStackInfo?: {
    serviceStackName: string;
    serviceStackTypeName: string;
    serviceStackTypeVersionName: string;
  };
}

export interface DnsCheckChallenge {
  challengeHost: string;
  challengeUrl: string;
  error: string;
  httpResponse: string;
  httpStatusCode: number;
}

export interface LastDnsCheckDetail {
  challenges: DnsCheckChallenge[];
  resolvedIpAddresses: string[];
  resolvedNameServers: string[];
  wildcardCnameCheckError?: string;
}

export interface BeingInstalledSslCertificate {
  registeredDomain: string;
  domainList: string[];
}

export interface DeployedSslCertificate {
  validFrom: string;
  validTill: string;
  registeredDomain: string;
  domainList: string[];
}

export class Domain {
  domainName: string;
  // DNS check status
  dnsCheckStatus: DnsCheckStatuses;
  nextDnsCheckAt?: string;
  lastDnsCheckAt?: string;
  lastDnsCheckDetail?: LastDnsCheckDetail;
  // SSL certificate installation status
  sslStatus?: SslStatuses;
  sslCertificateId?: string;
  sslCertificateInstallationError?: string;
  beingInstalledSslCertificate?: BeingInstalledSslCertificate;
  // Deployed SSL certificate
  deployedSslCertificate?: DeployedSslCertificate;
  // CDN status
  cdnStatus?: CdnStatuses;
}

export enum SslStatuses {
  Inactive = 'INACTIVE',
  WaitingForDns = 'WAITING_FOR_DNS',
  BeingInstalled = 'BEING_INSTALLED',
  BeingRenewed = 'BEING_RENEWED',
  Active = 'ACTIVE'
}

export enum CdnStatuses {
  Disabled = 'DISABLED',
  BeingInstalled = 'BEING_INSTALLED',
  Active = 'ACTIVE',
  BeingDisabled = 'BEING_DISABLED'
}

export enum DnsCheckStatuses {
  Pending = 'PENDING',
  Checking = 'CHECKING',
  Ok = 'OK',
  Failed = 'FAILED'
}

export class HttpRouting {
  clientId: string;
  created: string;
  lastUpdate: string;
  id: string;
  deleteOnSync: boolean;
  isSynced: boolean;
  isEditable: boolean;
  lastSync: string;
  locations: HttpRoutingLocation[];
  projectId: string;
  sslEnabled: boolean;
  cdnEnabled: boolean;
  domains: Domain[];
}

export class HttpRoutingPost {
  locations: HttpRoutingLocation[];
  sslEnabled: boolean;
  cdnEnabled: boolean;
  domains: string[];
  projectId?: string;
}

export class HttpRoutingPut {
  locations: Partial<HttpRoutingLocation>[];
  sslEnabled: boolean;
  cdnEnabled: boolean;
  domains: string[];
  projectId?: string;
}

export class HttpRoutingWithLocations extends HttpRouting {
  _stackLocations: HttpRoutingLocation[];
  _otherLocations: HttpRoutingLocation[];
}

export class HttpPortsWithServiceStackInfo {
  serviceStackName: string;
  serviceStackId: string;
  port: number;
}

export interface HttpRoutingLocationAccessPolicy {
  cidr: string[];
  defaultPolicy?: 'allow' | 'deny';
  enabled?: boolean;
}

export interface HttpRoutingLocationBasicAuthUser {
  comment: string;
  password: string;
  username: string;
}

export interface HttpRoutingLocationBasicAuth {
  enabled?: boolean;
  realm?: string;
  users: HttpRoutingLocationBasicAuthUser[];
}

export interface HttpRoutingLocationContent {
  code?: number;
  content?: string;
  contentType?: string;
  enabled?: boolean;
}

export interface HttpRoutingLocationRateLimiting {
  burst?: number;
  enabled?: boolean;
  key?: 'binary_remote_addr' | 'server_name';
  rate?: number;
  zone: string;
  zoneSize?: number;
}

export interface HttpRoutingLocationRedirect {
  code?: number;
  enabled?: boolean;
  preservePath?: boolean;
  preserveQuery?: boolean;
  to: string;
}

export interface HttpRoutingLocationConfig {
  accessPolicy?: HttpRoutingLocationAccessPolicy;
  basicAuth?: HttpRoutingLocationBasicAuth;
  content?: HttpRoutingLocationContent;
  rateLimiting?: HttpRoutingLocationRateLimiting;
  redirect?: HttpRoutingLocationRedirect;
}

